import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { GitMerge, Save, RefreshCw, X } from 'lucide-react';
import { ConcurrentEditConflict } from '../contexts/PatientContext';

interface Props {
  conflict: ConcurrentEditConflict;
  onResolve: (choice: 'local' | 'remote') => void;
}

/** Fields shown in the diff — label + accessor */
const DIFF_FIELDS: Array<{ label: string; key: keyof ConcurrentEditConflict['localPatient'] }> = [
  { label: 'Bed',       key: 'bed'           },
  { label: 'Ward',      key: 'ward'          },
  { label: 'Diagnosis', key: 'diagnosis'     },
  { label: 'Status',    key: 'patientStatus' },
  { label: 'PAC',       key: 'pacStatus'     },
  { label: 'Procedure', key: 'procedure'     },
  { label: 'DOS',       key: 'dos'           },
];

const ConcurrentEditModal: React.FC<Props> = ({ conflict, onResolve }) => {
  const { localPatient, remotePatient } = conflict;
  const dialogRef = useRef<HTMLDivElement>(null);

  const changedFields = DIFF_FIELDS.filter(
    f => String(localPatient[f.key] ?? '') !== String(remotePatient[f.key] ?? ''),
  );

  // Focus trap: Tab/Shift-Tab stays inside dialog; Escape → keep server version
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    el.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onResolve('remote'); return; }
      if (e.key !== 'Tab') return;

      const focusable = el.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) { e.preventDefault(); return; }
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onResolve]);

  // This dialog is rendered in-tree wherever a patient save happens to fail
  // (a save button, an inline edit sheet, a round-mode form), so it can end up
  // nested under whichever fixed-position ancestor was open at that moment.
  // A portal to <body> plus the app's highest z-index guarantees it's always
  // the actual top hit-test target — not just the topmost-looking element —
  // and the scroll lock stops the page underneath from intercepting a touch
  // that was meant to land on a button.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
      {/* Tapping outside keeps the server version — mobile users have no
          Escape key, so the backdrop must be an exit too. */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => onResolve('remote')}
      />

      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ced-title"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg outline-none"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <GitMerge className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 id="ced-title" className="font-bold text-slate-900">Conflicting Changes</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              <strong>{remotePatient.name}</strong> was saved by another user while you were editing.
            </p>
          </div>
        </div>

        {/* Diff table */}
        <div className="px-6 py-4 overflow-y-auto max-h-64">
          {changedFields.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No field differences detected — timestamps diverged only.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="text-left pb-2 w-24">Field</th>
                  <th className="text-left pb-2">Your version</th>
                  <th className="text-left pb-2">Server version</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {changedFields.map(({ label, key }) => (
                  <tr key={key}>
                    <td className="py-1.5 font-medium text-slate-500 pr-2">{label}</td>
                    <td className="py-1.5 pr-2">
                      <span className="px-1.5 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-mono">
                        {String(localPatient[key] ?? '—')}
                      </span>
                    </td>
                    <td className="py-1.5">
                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-800 rounded text-xs font-mono">
                        {String(remotePatient[key] ?? '—')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 pt-2 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => onResolve('remote')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 transition-colors text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Use server version
          </button>
          <button
            type="button"
            onClick={() => onResolve('local')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors text-sm"
          >
            <Save className="w-4 h-4" />
            Force-save my version
          </button>
        </div>

        <button
          onClick={() => onResolve('remote')}
          className="absolute top-4 right-4 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          aria-label="Dismiss — keep server version"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default ConcurrentEditModal;
